import './styles/site.css';
import './styles/blog.css';
import './styles/unite-go.css';
import './styles/home.css';
import './styles/unite-base.css';
import { detectLang, persistLang, setLang, syncLangInUrl, t, type Lang } from './i18n';
import { applyPageSeo, type SeoPage } from './seo';
import { fetchPageSeoMap, getPageSeoMeta } from './seo/page-seo-api';
import { resolveOgImage } from './seo/image-url';
import { fetchBlogPost, recordBlogView } from './blog/api';
import { blogSeoMeta, renderBlogListSection, renderBlogPostError, renderBlogPostHtml } from './blog/ui';
import {
  blogListPath,
  blogPostPath,
  getRoute,
  homePath,
  type AppRoute,
} from './routes';
import {
  UNITE_GO_META,
  UNITE_GO_PATH_EN,
  UNITE_GO_PATH_ZH,
  uniteGoPath,
  renderUniteGoPage,
} from './pages/unite-go';
import { renderHomePage } from './pages/home';
import {
  UNITE_BASE_META,
  UNITE_BASE_PATH_EN,
  UNITE_BASE_PATH_ZH,
  uniteBasePath,
  renderUniteBasePage,
} from './pages/unite-base';
import { hasValidConsent, saveConsent } from './utils/cookies';
import { CONTACT_ENDPOINT } from './utils/urls';
import {
  mountParallax,
  mountCounters,
  mountCardSpotlight,
  mountScrollProgress,
  mountUniteBasePillars,
} from './effects';

let disposeUniteBasePillars: (() => void) | null = null;

function seoPageForRoute(page: AppRoute['page']): SeoPage {
  if (page === 'unite-base') return 'unite-base';
  if (page === 'unite-go') return 'unite-go';
  return 'home';
}

let route: AppRoute = getRoute();
let lang: Lang = route.forcedLang ?? detectLang();
let blogViewTracked = '';
setLang(lang);
persistLang(lang);
if (route.page === 'home') syncLangInUrl(lang);

function renderCookieInventory(copy: ReturnType<typeof t>): string {
  const c = copy.cookie;
  return `
    <section class="modal__section modal__section--cookies">
      <h3>${esc(c.inventoryTitle)}</h3>
      <div class="cookie-inventory" role="table">
        ${c.inventory
          .map(
            (row) => `
          <div class="cookie-inventory__row" role="row">
            <div class="cookie-inventory__cell cookie-inventory__cell--name" role="cell"><strong>${esc(row.name)}</strong></div>
            <div class="cookie-inventory__cell" role="cell">${esc(row.purpose)}</div>
            <div class="cookie-inventory__cell cookie-inventory__cell--meta" role="cell">
              <span>${esc(row.duration)}</span>
              <span>${esc(row.type)}</span>
            </div>
          </div>`,
          )
          .join('')}
      </div>
    </section>`;
}

function renderCookieSettingsModal(copy: ReturnType<typeof t>): string {
  const c = copy.cookie;
  return `
    <div class="modal-backdrop" id="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
      <div class="modal modal--cookie">
        <h2 id="cookie-modal-title">${esc(c.settingsTitle)}</h2>
        <p class="modal__lead">${esc(c.lead)}</p>
        ${renderCookieInventory(copy)}
        <button type="button" class="btn btn-primary modal__close" id="cookie-modal-close">${esc(c.acknowledge)}</button>
      </div>
    </div>`;
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

async function render(): Promise<void> {
  await fetchPageSeoMap();
  const copy = t(lang);
  const isZh = lang === 'zh';
  const isBlog = route.page === 'blog-list' || route.page === 'blog-post';

  let seoOptions: Parameters<typeof applyPageSeo>[4];

  let metaOverride =
    route.page === 'unite-base'
      ? getPageSeoMeta('unite-base', lang) ?? UNITE_BASE_META[lang]
      : route.page === 'unite-go'
        ? getPageSeoMeta('unite-go', lang) ?? UNITE_GO_META[lang]
        : route.page === 'blog-list'
          ? getPageSeoMeta('blog-list', lang) ?? blogSeoMeta(lang, 'list')
          : route.page === 'home'
            ? getPageSeoMeta('home', lang) ?? undefined
            : undefined;

  let mainContent = '';
  if (route.page === 'blog-list') {
    seoOptions = {
      canonicalPaths: { en: '/blog', zh: '/zh/blog' },
    };
    mainContent = await renderBlogListSection(lang, copy);
  } else if (route.page === 'blog-post' && route.blogId) {
    try {
      const post = await fetchBlogPost(lang, route.blogId);
      metaOverride = blogSeoMeta(lang, 'post', post);
      seoOptions = {
        canonicalPaths: {
          en: blogPostPath('en', route.blogId),
          zh: blogPostPath('zh', route.blogId),
        },
        ogImage: resolveOgImage(location.origin, post.og_image || post.cover_image),
        ogType: 'article',
        article: { publishedAt: post.published_at, modifiedAt: post.updated_at },
      };
      mainContent = renderBlogPostHtml(post, lang, copy);
    } catch (e) {
      mainContent = renderBlogPostError(lang, copy, String(e).includes('not_found'));
    }
  }

  applyPageSeo(lang, copy, seoPageForRoute(route.page), metaOverride, seoOptions);

  const navProductsHref = `${homePath(lang)}#products`;

  const siteBgClass =
    route.page === 'home'
      ? ' site-bg--home'
      : route.page === 'unite-go'
        ? ' site-bg--unite-go'
        : route.page === 'unite-base'
          ? ' site-bg--unite-base'
          : '';

  const app = document.getElementById('app');
  if (!app) return;
  app.setAttribute('aria-busy', 'true');

  app.innerHTML = `
    <div class="scroll-progress" id="scroll-progress" aria-hidden="true"></div>

    <div class="site-bg${siteBgClass}" aria-hidden="true">
      <div class="site-bg__grid"></div>
      <div class="site-bg__beam"></div>
      <div class="site-bg__orb site-bg__orb--1"></div>
      <div class="site-bg__orb site-bg__orb--2"></div>
      <div class="site-bg__orb site-bg__orb--3"></div>
    </div>

    <header class="site-header" id="site-header">
      <div class="site-header__inner">
        <a href="${homePath(lang)}" class="site-brand" aria-label="UniteStream">
          <img class="site-brand__logo" src="/images/logo.png" width="621" height="152" alt="UniteStream" />
        </a>
        <nav class="site-nav" aria-label="Primary">
          <a href="${navProductsHref}"${route.page === 'home' ? ' class="is-active"' : ''}>${esc(copy.nav.products)}</a>
          <a href="${uniteBasePath(lang)}"${route.page === 'unite-base' ? ' class="is-active"' : ''}>${esc(copy.nav.uniteBase)}</a>
          <a href="${uniteGoPath(lang)}"${route.page === 'unite-go' ? ' class="is-active"' : ''}>${esc(copy.nav.uniteGo)}</a>
          <a href="${blogListPath(lang)}"${isBlog ? ' class="is-active"' : ''}>${esc(copy.nav.blog)}</a>
          <a href="#contact">${esc(copy.nav.contact)}</a>
        </nav>
        <div class="site-actions">
          <button type="button" class="lang-btn${isZh ? ' is-active' : ''}" id="lang-zh" aria-label="${esc(copy.nav.languageAria)}">中</button>
          <button type="button" class="lang-btn${!isZh ? ' is-active' : ''}" id="lang-en" aria-label="${esc(copy.nav.languageAria)}">EN</button>
        </div>
      </div>
    </header>

    <main>
      ${
        route.page === 'blog-list' || route.page === 'blog-post'
          ? mainContent
          : route.page === 'unite-base'
            ? renderUniteBasePage(copy, lang, esc)
            : route.page === 'unite-go'
              ? renderUniteGoPage(copy, lang, esc)
              : renderHomePage(copy, lang, esc)
      }

      <section class="section section--contact" id="contact">
        <div class="section__inner contact reveal">
          <div class="contact__intro">
            <p class="section__eyebrow">${esc(copy.contact.eyebrow)}</p>
            <h2 class="section__title">${esc(copy.contact.title)}</h2>
            <p class="section__subtitle">${esc(copy.contact.subtitle)}</p>
          </div>
          <form class="contact-form" id="contact-form" novalidate>
            <div class="contact-form__row">
              <label class="field">
                <span class="field__label">${esc(copy.contact.labels.name)}</span>
                <input class="field__input" type="text" name="name" autocomplete="name" required placeholder="${esc(copy.contact.placeholders.name)}" />
              </label>
              <label class="field">
                <span class="field__label">${esc(copy.contact.labels.email)}</span>
                <input class="field__input" type="email" name="email" autocomplete="email" required placeholder="${esc(copy.contact.placeholders.email)}" />
              </label>
            </div>
            <label class="field">
              <span class="field__label">${esc(copy.contact.labels.company)}</span>
              <input class="field__input" type="text" name="company" autocomplete="organization" placeholder="${esc(copy.contact.placeholders.company)}" />
            </label>
            <label class="field">
              <span class="field__label">${esc(copy.contact.labels.message)}</span>
              <textarea class="field__input field__input--area" name="message" rows="4" placeholder="${esc(copy.contact.placeholders.message)}"></textarea>
            </label>
            <div class="contact-form__footer">
              <button type="submit" class="btn btn-primary contact-form__submit" id="contact-submit">${esc(copy.contact.submit)}</button>
              <p class="contact-form__note">${esc(copy.contact.note)}</p>
            </div>
            <p class="contact-form__status" id="contact-status" role="status" aria-live="polite"></p>
          </form>
        </div>
      </section>

    </main>

    <footer class="site-footer">
      <div class="site-footer__inner">
        <div class="site-footer__brand">
          <a href="#" class="site-brand" aria-label="${esc(copy.footer.product)}">
            <img class="site-brand__logo site-brand__logo--footer" src="/images/logo.png" width="621" height="152" alt="${esc(copy.footer.product)}" />
          </a>
          <p>${esc(copy.footer.tagline)}</p>
          <p class="site-footer__copy">© ${new Date().getFullYear()} UniteStream. ${esc(copy.footer.rights)}</p>
        </div>
        <nav class="site-footer__links" aria-label="Footer">
          <a href="${uniteBasePath(lang)}">${esc(copy.nav.uniteBase)}</a>
          <a href="${uniteGoPath(lang)}">${esc(copy.nav.uniteGo)}</a>
          <a href="${blogListPath(lang)}">${esc(copy.nav.blog)}</a>
          <a href="#contact">${esc(copy.nav.contact)}</a>
          <a href="#" id="privacy-link">${esc(copy.footer.privacy)}</a>
          <a href="#" id="cookie-settings">${esc(copy.footer.cookies)}</a>
        </nav>
      </div>
    </footer>

    <div class="cookie-banner" id="cookie-banner" role="dialog" aria-labelledby="cookie-title" aria-describedby="cookie-desc" aria-live="polite">
      <span class="cookie-banner__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l7 3v5c0 4.6-3 7.7-7 9-4-1.3-7-4.4-7-9V6l7-3z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
      </span>
      <div class="cookie-banner__content">
        <h3 id="cookie-title">${esc(copy.cookie.title)}</h3>
        <p id="cookie-desc">${esc(copy.cookie.lead)}</p>
        <p class="cookie-banner__links">
          <a href="#" id="cookie-learn">${esc(copy.cookie.learnMore)}</a>
          <span aria-hidden="true"> · </span>
          <a href="#" id="cookie-customize">${esc(copy.footer.cookies)}</a>
        </p>
        <div class="cookie-banner__actions">
          <button type="button" class="btn btn-primary cookie-banner__btn-eq" id="cookie-accept">${esc(copy.cookie.acknowledge)}</button>
        </div>
      </div>
    </div>

    ${renderCookieSettingsModal(copy)}

    <div class="modal-backdrop" id="privacy-modal" role="dialog" aria-modal="true" aria-labelledby="privacy-modal-title">
      <div class="modal">
        <h2 id="privacy-modal-title">${esc(copy.footer.privacy)}</h2>
        <div class="modal__body">
          ${copy.privacy.sections
            .map(
              (s) => `
            <section class="modal__section">
              <h3>${esc(s.title)}</h3>
              <p>${esc(s.body)}</p>
            </section>`,
            )
            .join('')}
        </div>
        ${renderCookieInventory(copy)}
        <button type="button" class="btn btn-primary modal__close" id="privacy-close">${esc(copy.privacy.close)}</button>
      </div>
    </div>
  `;

  app.setAttribute('aria-busy', 'false');
  bindEvents();
  setupScrollReveal();
  setupHeaderScroll();
  showCookieBannerIfNeeded();
  setupEffects();
  trackBlogViewOnce();
}

function trackBlogViewOnce(): void {
  if (route.page !== 'blog-post' || !route.blogId) return;
  const key = `${lang}:${route.blogId}`;
  if (blogViewTracked === key) return;
  blogViewTracked = key;
  void recordBlogView(lang, route.blogId);
}

function setupEffects(): void {
  disposeUniteBasePillars?.();
  disposeUniteBasePillars = null;

  const frame = document.getElementById('hero-frame');
  if (frame && route.page !== 'home') mountParallax(frame);

  const progress = document.getElementById('scroll-progress');
  if (progress) mountScrollProgress(progress);

  if (route.page === 'unite-go' || route.page === 'unite-base') {
    disposeUniteBasePillars = mountUniteBasePillars();
  }

  mountCounters();
  if (route.page === 'home') mountCardSpotlight('.ub-hub__card');
}

function bindEvents(): void {
  document.getElementById('lang-zh')?.addEventListener('click', () => switchLang('zh'));
  document.getElementById('lang-en')?.addEventListener('click', () => switchLang('en'));
  document.getElementById('cookie-accept')?.addEventListener('click', () => {
    saveConsent();
    hideCookieBanner();
    closeCookieModal();
  });
  document.getElementById('cookie-learn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openPrivacyModal();
  });
  document.getElementById('cookie-customize')?.addEventListener('click', (e) => {
    e.preventDefault();
    openCookieModal();
  });
  document.getElementById('cookie-settings')?.addEventListener('click', (e) => {
    e.preventDefault();
    openCookieModal();
  });
  document.getElementById('privacy-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    openPrivacyModal();
  });
  document.getElementById('cookie-modal-close')?.addEventListener('click', closeCookieModal);
  document.getElementById('cookie-modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeCookieModal();
  });
  document.getElementById('privacy-close')?.addEventListener('click', closePrivacyModal);
  document.getElementById('privacy-modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closePrivacyModal();
  });
  document
    .getElementById('contact-form')
    ?.addEventListener('submit', (e) => handleContactSubmit(e as SubmitEvent));
}

async function handleContactSubmit(e: SubmitEvent): Promise<void> {
  e.preventDefault();
  const form = e.currentTarget as HTMLFormElement;
  const copy = t(lang);
  const status = document.getElementById('contact-status');
  const submit = document.getElementById('contact-submit') as HTMLButtonElement | null;

  if (!form.reportValidity()) return;

  const data = new FormData(form);
  const payload = {
    name: String(data.get('name') ?? '').trim(),
    email: String(data.get('email') ?? '').trim(),
    company: String(data.get('company') ?? '').trim(),
    message: String(data.get('message') ?? '').trim(),
    lang,
    referrer: document.referrer || undefined,
  };

  const setStatus = (text: string, kind: 'ok' | 'err' | '') => {
    if (!status) return;
    status.textContent = text;
    status.className = `contact-form__status${kind ? ` is-${kind}` : ''}`;
  };

  if (submit) {
    submit.disabled = true;
    submit.textContent = copy.contact.sending;
  }
  setStatus('', '');
  try {
    const res = await fetch(CONTACT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      form.reset();
      setStatus(copy.contact.success, 'ok');
    } else {
      setStatus(copy.contact.error, 'err');
    }
  } catch {
    setStatus(copy.contact.error, 'err');
  } finally {
    if (submit) {
      submit.disabled = false;
      submit.textContent = copy.contact.submit;
    }
  }
}

function switchLang(next: Lang): void {
  if (next === lang) return;
  if (route.page === 'unite-base') {
    location.href = next === 'zh' ? UNITE_BASE_PATH_ZH : UNITE_BASE_PATH_EN;
    return;
  }
  if (route.page === 'unite-go') {
    location.href = next === 'zh' ? UNITE_GO_PATH_ZH : UNITE_GO_PATH_EN;
    return;
  }
  if (route.page === 'blog-post' && route.blogId) {
    location.href = blogPostPath(next, route.blogId);
    return;
  }
  if (route.page === 'blog-list') {
    location.href = blogListPath(next);
    return;
  }
  lang = next;
  setLang(lang);
  persistLang(lang);
  syncLangInUrl(lang);
  void render();
}

function showCookieBannerIfNeeded(): void {
  if (hasValidConsent()) return;
  requestAnimationFrame(() => document.getElementById('cookie-banner')?.classList.add('is-visible'));
}

function hideCookieBanner(): void {
  document.getElementById('cookie-banner')?.classList.remove('is-visible');
}

function openCookieModal(): void {
  document.getElementById('cookie-modal')?.classList.add('is-open');
}

function closeCookieModal(): void {
  document.getElementById('cookie-modal')?.classList.remove('is-open');
}

function openPrivacyModal(): void {
  document.getElementById('privacy-modal')?.classList.add('is-open');
}

function closePrivacyModal(): void {
  document.getElementById('privacy-modal')?.classList.remove('is-open');
}

function setupHeaderScroll(): void {
  const header = document.getElementById('site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

function setupScrollReveal(): void {
  const els = document.querySelectorAll('.reveal');
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -32px 0px' },
  );
  els.forEach((el) => io.observe(el));
}

window.addEventListener('popstate', () => {
  route = getRoute();
  lang = route.forcedLang ?? detectLang();
  setLang(lang);
  persistLang(lang);
  blogViewTracked = '';
  void render();
});

void render();
